/**
 * useTabFromURL — Wiz-pattern bookmarkable tab state.
 *
 * Drop-in replacement for `useState<TabId>('default')` that syncs to/from
 * the URL `?tab=...` query parameter. Bookmarks land on the right tab,
 * browser back/forward navigates between tabs, and switching tabs updates
 * the URL so users can share/bookmark a specific section.
 *
 * Usage::
 *
 *   import { useTabFromURL } from '../../hooks/useTabFromURL';
 *
 *   const [activeTab, setActiveTab] = useTabFromURL<TabId>(
 *     ['identities', 'permissions', 'risks'],  // valid tab IDs
 *     'identities',                             // default tab
 *   );
 *
 * Behaviour:
 *   - Initial tab read from `?tab=` if present and valid; falls back to default
 *   - `setActiveTab(next)` updates state AND URL in one call
 *   - URL stays clean for the default tab (no `?tab=default`)
 *   - Back/forward syncs state from URL automatically
 *   - Other query params preserved when switching tabs
 *
 * Same pattern as SettingsPage URL-sync (Wiz parity).
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useTabFromURL<TabId extends string>(
  validIds: readonly TabId[] | TabId[],
  defaultTab: TabId,
  paramName: string = 'tab',
): [TabId, (next: TabId) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const _validSet = new Set<string>(validIds as readonly string[]);
  const _initial = searchParams.get(paramName);
  const [activeTab, setActiveTabState] = useState<TabId>(
    _initial && _validSet.has(_initial) ? (_initial as TabId) : defaultTab,
  );

  // Sync from URL → state on back/forward
  useEffect(() => {
    const t = searchParams.get(paramName);
    if (t && _validSet.has(t) && t !== activeTab) {
      setActiveTabState(t as TabId);
    } else if (!t && activeTab !== defaultTab) {
      setActiveTabState(defaultTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Sync state → URL on tab click
  const setActiveTab = (next: TabId) => {
    setActiveTabState(next);
    const params = new URLSearchParams(searchParams);
    if (next === defaultTab) {
      params.delete(paramName);  // keep URL clean for default
    } else {
      params.set(paramName, next);
    }
    setSearchParams(params, { replace: false });
  };

  return [activeTab, setActiveTab];
}
